<script setup lang="ts">
import { storeToRefs } from 'pinia';

import Button from '@clovyr/bed/components/controls/Button.vue';
import { BackupAPI } from '@clovyr/pollen/backup';
import { isWNextProvider } from '@clovyr/pollen/compute/util';
import { addScheme, execWithOutput } from '@clovyr/pollen/remote_exec';
import { sleep } from '@clovyr/pollen/util/sleep';

import CheckboxBlock from '@/components/blocks/CheckboxBlock.vue';
import Modal from '@/components/elements/Modal.vue';
import Module from '@/components/elements/Module.vue';
import { useAppStatus } from '@/composables/useAppStatus';
import { useDocker } from '@/composables/useDocker';
import { useRemoteExec } from '@/composables/useRemoteExec';
import { useEventBus } from '@/stores/event_bus';
import { usePollenStore } from '@/stores/pollen_store';

import ProgressModal from './ProgressModal.vue';

const props = defineProps<{ id: string }>();

const deploymentID = toRef(props, 'id');
const { app, deployment, isRunning } = useAppStatus(deploymentID);
const { eventBus } = useEventBus();
const { pollen, getComputeProvider } = usePollenStore();
const { instances } = storeToRefs(usePollenStore());
const { exec, rawServices, clovyrAppPath } = useDocker(deploymentID);

const isRestarting = ref(false);
const isRebooting = ref(false);
const backupBeforeReboot = ref(true);
const showRebootConfirmation = ref(false);
const rebootProgress = ref('');
const actionsDisabled = computed(() => !isRunning.value || isRestarting.value || isRebooting.value);

const REBOOT_TIMEOUT_MS = 5 * 60 * 1000;
const APP_RECOVERY_GRACE_MS = 15000;

async function waitForReboot(
  host: string,
  accessKey: string,
  signal: AbortSignal,
  attempt: { generation: number; accepted: boolean },
) {
  const deadline = Date.now() + REBOOT_TIMEOUT_MS;
  let phase: 'shutdown' | 'instance' | 'app' = 'shutdown';
  let generation = attempt.generation;
  let instanceReadyAt: number | null = null;
  let recoveryAttempted = false;

  // Observe shutdown before accepting readiness, so the old boot cannot count as success.
  while (Date.now() < deadline) {
    signal.throwIfAborted();
    if (generation !== attempt.generation) {
      generation = attempt.generation;
      phase = 'shutdown';
      instanceReadyAt = null;
    }
    const path = phase === 'app' ? '/_leaf/ready?type=app' : '/_leaf/ready';
    let ready = false;
    try {
      const response = await fetch(`${addScheme(host)}${path}`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${accessKey}` },
        cache: 'no-store',
        redirect: 'error',
        signal: AbortSignal.any([
          signal,
          AbortSignal.timeout(Math.max(1, Math.min(10000, deadline - Date.now()))),
        ]),
      });
      ready = response.ok;
    } catch {
      // Connection failures are expected while the instance reboots.
    }
    signal.throwIfAborted();

    // A failed host attempt must not count as downtime for its provider fallback.
    if (generation !== attempt.generation) {
      continue;
    }

    if (phase === 'shutdown' && !ready) {
      phase = 'instance';
      rebootProgress.value = 'Instance is rebooting. Waiting for it to come back online.';
    } else if (phase === 'instance' && ready) {
      phase = 'app';
      instanceReadyAt = attempt.accepted ? Date.now() : null;
      rebootProgress.value = 'Instance is online. Waiting for the application to be ready.';
    } else if (phase === 'app' && attempt.accepted) {
      if (ready) {
        return;
      }
      // A queued provider command must not start containers on the old boot.
      instanceReadyAt ??= Date.now();
      if (!recoveryAttempted && Date.now() - instanceReadyAt >= APP_RECOVERY_GRACE_MS) {
        recoveryAttempted = true;
        rebootProgress.value = 'Application is not ready yet. Ensuring its containers are running.';
        try {
          const appPath = clovyrAppPath.value.replaceAll("'", "'\\''");
          await execWithOutput(
            host,
            accessKey,
            `cd -- '${appPath}' && docker compose up -d --no-recreate`,
            AbortSignal.any([
              signal,
              AbortSignal.timeout(Math.max(1, Math.min(30000, deadline - Date.now()))),
            ]),
          );
          signal.throwIfAborted();
          rebootProgress.value = 'Containers started. Waiting for the application to be ready.';
        } catch {
          signal.throwIfAborted();
          rebootProgress.value =
            'Could not confirm the containers are running. Continuing to check application readiness.';
          eventBus.emit('toast:show', {
            type: 'info',
            message:
              'The container recovery command failed. We will keep checking application readiness; check Console Access & Logs if it does not recover.',
          });
        }
      }
    }

    await sleep(Math.max(0, Math.min(phase === 'shutdown' ? 1000 : 5000, deadline - Date.now())));
  }

  throw new Error(
    'The instance did not finish rebooting and pass the app-ready check within five minutes. Check Console Access & Logs for its current status.',
  );
}

const doReboot = async (withBackup: boolean) => {
  if (actionsDisabled.value) {
    return;
  }

  isRebooting.value = true;
  const controller = new AbortController();
  try {
    const { creds, ensureHostLogin } = useRemoteExec(deployment.value, app.value);
    if (withBackup) {
      rebootProgress.value = 'Creating a backup before rebooting. This may take a few minutes.';
      try {
        if (!(await ensureHostLogin())) {
          throw new Error('Unable to connect to the instance');
        }

        const snapshotID = await new BackupAPI(
          creds.value.host,
          creds.value.accessKey,
          deployment.value.appID,
        ).createSnapshot(['keep']);
        deployment.value.backups ||= [];
        deployment.value.backups.push({
          snapshotID,
          createdAt: Date.now(),
          name: 'Snapshot before reboot',
          appVersion: deployment.value.appVersion!,
        });
        await pollen.putDeployment(deployment.value);
      } catch (error) {
        throw new Error(
          `Backup failed. To reboot without a backup, uncheck “Back up before reboot” and try again. ${error instanceof Error ? error.message : error}`,
          { cause: error },
        );
      }
    }

    rebootProgress.value = 'Rebooting the instance now.';
    const attempt = { generation: 0, accepted: false };
    const requestReboot = async () => {
      try {
        // Bearer authentication does not depend on a working host-login endpoint.
        // Bound the attempt so an unresponsive leaf cannot block provider fallback.
        await execWithOutput(
          creds.value.host,
          creds.value.accessKey,
          'systemd-run --on-active=2s -- /sbin/shutdown -r now',
          AbortSignal.timeout(15000),
        );
      } catch {
        attempt.generation += 1;
        rebootProgress.value =
          'Host reboot command failed. Requesting a reboot through the cloud provider.';
        const provider = getComputeProvider(deployment.value);
        const instance = instances.value.find((i) => i.fqdn === deployment.value.fqdn);
        const id = isWNextProvider(provider) ? deployment.value.fqdn : instance?.remoteinstanceid;
        if (!id) {
          throw new Error(
            'Cloud instance details are unavailable. Reboot through your provider console.',
          );
        }
        await provider.rebootInstance({
          id,
          region: instance?.instance_region ?? deployment.value.region,
        });
      }
      attempt.accepted = true;
    };
    // Observe downtime while the provider command is in flight, rather than
    // starting polling after a fast reboot has already completed.
    await Promise.all([
      requestReboot(),
      waitForReboot(creds.value.host, creds.value.accessKey, controller.signal, attempt),
    ]);
    rebootProgress.value = 'Instance reboot complete. Your application is ready.';
    eventBus.emit('toast:show', {
      type: 'success',
      message: rebootProgress.value,
    });
  } catch (error) {
    rebootProgress.value = `Error rebooting instance: ${error instanceof Error ? error.message : error}`;
    eventBus.emit('toast:show', {
      type: 'error',
      message: rebootProgress.value,
    });
  } finally {
    controller.abort();
    isRebooting.value = false;
  }
};

const onClickRebootInstance = () => {
  if (actionsDisabled.value) {
    return;
  }
  backupBeforeReboot.value = true;
  showRebootConfirmation.value = true;
};

const onConfirmReboot = async () => {
  showRebootConfirmation.value = false;
  await doReboot(backupBeforeReboot.value);
};

const doRestart = async () => {
  const svcs = rawServices.value?.values().toArray().join(' ') || '';

  try {
    await exec(`
pushd ${clovyrAppPath.value}
docker compose stop ${svcs} && docker compose up -d --remove-orphans ${svcs}
    `);
  } catch (error) {
    eventBus.emit('toast:show', {
      type: 'error',
      message: `Error restarting services: ${error instanceof Error ? error.message : error}`,
    });
  }
};

const onClickRestartServices = () => {
  eventBus.emit('modal:confirm:show', {
    title: 'Restart Services',
    text: 'Are you sure you want to restart all services? Your application will be unavailable until the process completes.\n\nUse this when your services are misbehaving or you have made changes to the configuration that require a restart.',
    okLabel: 'Restart',
    cancelLabel: 'Cancel',
    resolve: async (confirmed) => {
      if (confirmed && !actionsDisabled.value) {
        isRestarting.value = true;
        await doRestart();
        isRestarting.value = false;
      }
    },
  });
};
</script>
<template>
  <Module class="module--appessentials app-essentials" :bigHead="true" hed="Troubleshooting">
    <template #default>
      <div class="row">
        <Button
          label="Console Access & Logs"
          :link="`/library/${id}/troubleshoot`"
          :disabled="actionsDisabled"
        />
        <Button
          class="restart button--danger"
          label="Restart Services"
          :disabled="actionsDisabled"
          @click="onClickRestartServices"
        />
        <Button
          class="restart button--danger"
          :label="isRebooting ? 'Rebooting Instance' : 'Reboot Instance'"
          :disabled="actionsDisabled"
          @click="onClickRebootInstance"
        />
      </div>
      <p v-if="rebootProgress && !isRebooting" role="status">{{ rebootProgress }}</p>
    </template>
  </Module>

  <Modal
    v-if="showRebootConfirmation"
    heading-text="Reboot Instance"
    size="compact"
    @close="showRebootConfirmation = false"
  >
    <template #body>
      <p>
        Restart the server and all its services. Your application and console will be unavailable
        for a few minutes. Active connections and running tasks will be interrupted. Save your work
        first.
      </p>
      <p>
        If the server does not respond, we will request a cloud-provider reset. Unsaved data may be
        lost. We will check for recovery for up to five minutes.
      </p>
      <div class="reboot-backup-option">
        <CheckboxBlock
          :isToggleSwitch="true"
          :value="backupBeforeReboot"
          title="Back up before reboot"
          :description="
            backupBeforeReboot
              ? 'Create a backup first. If the backup fails, the reboot will stop.'
              : 'No backup will be created before rebooting. Recent changes may be lost.'
          "
          @update:modelValue="backupBeforeReboot = $event"
        />
      </div>
    </template>
    <template #controls-left>
      <Button label="Cancel" @click="showRebootConfirmation = false" />
    </template>
    <template #controls-right>
      <Button
        class="button--danger"
        :label="backupBeforeReboot ? 'Back Up & Reboot' : 'Reboot Without Backup'"
        @click="onConfirmReboot"
      />
    </template>
  </Modal>

  <ProgressModal v-if="isRebooting" heading-text="Rebooting Instance" :body="rebootProgress" />
  <ProgressModal
    v-if="isRestarting"
    heading-text="Restarting Services"
    body="Your application services are restarting. This may take a few minutes."
    fast
  />
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/layout/grid-vars';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.app-essentials {
  :deep(.module__head) {
    background-color: rgba(132, 115, 152, 0.1);
  }
  :deep(.module__body) {
    .module__statinline {
      margin-top: grid-vars.space(2);
      margin-bottom: grid-vars.space(1);

      .button__label {
        @include breakpoints.media-breakpoint-only(md) {
          font-size: 11px;
        }
      }
    }
  }
}
.restart {
  margin-top: 1rem;
}

.reboot-backup-option {
  padding-top: grid-vars.space(3);
}
</style>
